import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import stringHash from "@mapmycustomers/shared/util/hash/stringHash";
import { LoadingSpinner } from "@mapmycustomers/ui";

import FeaturedMap from "@app/component/map/FeaturedMap";
import CardType from "@app/enum/dashboard/CardType";
import PreviewType from "@app/enum/dashboard/PreviewType";
import ViewMode from "@app/enum/dashboard/ViewMode";
import BaseCard from "@app/scene/dashboard/components/cards/common/BaseCard";
import GoalInformation from "@app/scene/dashboard/components/cards/common/GoalInformation";
import Menu from "@app/scene/dashboard/components/cards/common/Menu";
import ViewModeSelector from "@app/scene/dashboard/components/cards/common/ViewModeSelector";
import {
  fetchUpcomingActivitiesCardData,
  hideUpcomingActivitiesDrillDown,
  showUpcomingActivitiesDrillDown,
} from "@app/scene/dashboard/store/actions";
import { getUpcomingActivitiesDrillDownConfiguration } from "@app/scene/dashboard/store/cards/upcomingActivities/selectors";
import UpcomingActivitiesCardData from "@app/scene/dashboard/store/cards/upcomingActivities/UpcomingActivitiesCardData";
import CardContentProps from "@app/scene/dashboard/types/CardContentProps";
import { DEFAULT_VIEW_PORT } from "@app/scene/dashboard/utils/consts";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import UpcomingActivitiesCardConfiguration from "@app/types/dashboard/cards/upcomingActivities/UpcomingActivitiesCardConfiguration";
import UpcomingActivitiesViewState from "@app/types/dashboard/cards/upcomingActivities/UpcomingActivitiesViewState";
import MapViewportState from "@app/types/map/MapViewportState";
import applyBaseMapStyle from "@app/util/map/applyBaseMapStyle";

import cardMessages from "../messages";

import styles from "./CardContentComponent.module.scss";
import DrillDown from "./DrillDown";
import ListContent from "./ListContent";
import MapContent from "./MapContent";
import messages from "./messages";
import sampleData from "./sample.json";

// We know types are compatible in sampleData
// @ts-ignore
const SAMPLE_DATA: UpcomingActivitiesCardData = sampleData;

interface Props
  extends CardContentProps<UpcomingActivitiesCardConfiguration, UpcomingActivitiesViewState> {
  drillDownVisible: boolean;
  mapSettings: {
    style: BaseMapStyle;
  };
  onFetchData?: typeof fetchUpcomingActivitiesCardData;
  onHideDrillDown: () => void;
  onShowDrillDown: typeof showUpcomingActivitiesDrillDown.request;
}

const CardContentComponent: React.FC<Props> = ({
  configuration,
  draggable,
  drillDownVisible,
  fullWidth,
  mapSettings,
  onChangeView,
  onCopy,
  onEdit,
  onFetchData,
  onHideDrillDown,
  onRemove,
  onShowDrillDown,
  preview,
  scope,
  viewMode,
}) => {
  const intl = useIntl();

  // TODO: this shouldn't be a state, we should take viewMode from outside only, but currently this is a temporary
  // implementation because we don't store cards viewState
  const [currentViewMode, setCurrentViewMode] = useState(
    viewMode ?? configuration.preferences.defaultMode
  );
  useEffect(() => {
    setCurrentViewMode(configuration.preferences.defaultMode);
  }, [configuration.preferences.defaultMode]);

  const configurationKey = stringHash(JSON.stringify(configuration));

  const [listData, setListData] = useState<undefined | UpcomingActivitiesCardData>(
    preview === PreviewType.SAMPLE_DATA ? SAMPLE_DATA : undefined
  );

  const [mapData, setMapData] = useState<undefined | UpcomingActivitiesCardData>(
    preview === PreviewType.SAMPLE_DATA ? SAMPLE_DATA : undefined
  );

  const loading = useRef(false);
  const handleLoadMore = useCallback(() => {
    if (preview !== PreviewType.SAMPLE_DATA && listData?.activities && !loading.current) {
      loading.current = true;
      onFetchData?.({
        callback: (newData: UpcomingActivitiesCardData) => {
          loading.current = false;
          setListData((oldData) => ({
            // append new entities
            activities: [...(oldData?.activities ?? []), ...(newData.activities ?? [])],
            total: newData.total,
          }));
        },
        configuration,
        failureCallback: () => {
          loading.current = false;
        },
        offset: listData.activities.length,
        scope,
        viewMode: ViewMode.LIST,
      });
    }
  }, [configuration, listData, onFetchData, preview, scope]);

  const handleChangeViewMode = useCallback(
    (viewMode) => {
      setCurrentViewMode(viewMode);
      onChangeView?.({ viewMode });
    },
    [onChangeView]
  );

  const handleViewData = useCallback(() => {
    onShowDrillDown?.({ configuration, scope });
  }, [configuration, onShowDrillDown, scope]);

  const handleMapInitialized = useCallback(
    (map: google.maps.Map) => {
      applyBaseMapStyle(map, mapSettings.style);
    },
    [mapSettings.style]
  );

  const handleChangeFullWidth = useCallback(
    (fullWidth: boolean) => {
      onChangeView?.({ fullWidth });
    },
    [onChangeView]
  );

  const handleViewportChange = useCallback(
    (viewport: MapViewportState) => {
      if (preview !== PreviewType.SAMPLE_DATA) {
        onFetchData?.({
          callback: setMapData,
          configuration,
          scope,
          viewMode: ViewMode.MAP,
          viewport,
        });
      }
    },
    [configuration, onFetchData, preview, scope]
  );

  useEffect(() => {
    if (preview !== PreviewType.SAMPLE_DATA) {
      onFetchData?.({
        callback: setListData,
        configuration,
        scope,
        viewMode: ViewMode.LIST,
      });

      onFetchData?.({
        callback: setMapData,
        configuration,
        scope,
        viewMode: ViewMode.MAP,
        viewport: DEFAULT_VIEW_PORT,
      });
    }
  }, [configuration, onFetchData, preview, scope]);

  const cardSubtitle = useMemo(() => {
    if (listData?.total === undefined || mapData?.total === undefined) {
      return null;
    }

    switch (currentViewMode) {
      case ViewMode.LIST:
        return intl.formatMessage(messages.listSubtitle, {
          count: listData.total,
        });
      case ViewMode.MAP:
        return intl.formatMessage(messages.mapSubtitle, {
          count: mapData.total,
          totalCount: listData.total,
        });
    }
  }, [intl, currentViewMode, listData?.total, mapData?.total]);

  return (
    <BaseCard
      actions={<ViewModeSelector onChange={handleChangeViewMode} value={currentViewMode} />}
      contentClassName={styles.container}
      draggable={draggable}
      menu={
        <Menu
          expanded={fullWidth}
          onCopy={onCopy}
          onEdit={onEdit}
          onRemove={onRemove}
          onResize={preview !== undefined ? undefined : handleChangeFullWidth}
          onViewData={preview !== undefined ? undefined : handleViewData}
        />
      }
      subtitle={<div className={styles.subtitle}>{cardSubtitle}</div>}
      title={
        configuration.customTitle?.trim() ||
        intl.formatMessage(cardMessages.cardTitle, {
          date: (text: string[]) => text,
          dateRange: configuration.dateRange.range,
          dateSubRange: configuration.dateRange.subRange,
        })
      }
    >
      {!listData || !mapData ? (
        <LoadingSpinner />
      ) : (
        <>
          {currentViewMode === ViewMode.MAP ? (
            <FeaturedMap
              className={styles.map}
              initialOptions={{
                center: { lat: 0, lng: 0 },
                clickableIcons: false,
                controlSize: 32,
                disableDefaultUI: true,
                gestureHandling: "greedy",
                zoom: 1,
              }}
              onInitialized={handleMapInitialized}
            >
              <MapContent
                configuration={configuration}
                configurationKey={configurationKey}
                data={mapData}
                onViewportChange={handleViewportChange}
                scope={scope}
              />
            </FeaturedMap>
          ) : (
            <ListContent
              data={listData}
              key={configurationKey}
              onLoadMore={handleLoadMore}
              total={listData.total}
            />
          )}
          {!!configuration.goal?.enabled && (
            <GoalInformation
              goalConfiguration={configuration.goal}
              scope={scope}
              type={CardType.UPCOMING_ACTIVITIES}
              value={mapData.total}
            />
          )}
        </>
      )}
      {drillDownVisible && (
        <DrillDown
          configuration={configuration}
          onEdit={onEdit}
          onHide={onHideDrillDown}
          scope={scope}
        />
      )}
    </BaseCard>
  );
};

const mapStateToProps = (
  state: RootState,
  props: CardContentProps<UpcomingActivitiesCardConfiguration, UpcomingActivitiesViewState>
) => ({
  drillDownVisible:
    getUpcomingActivitiesDrillDownConfiguration(state) ===
    (props.temporaryConfiguration ?? props.configuration),
  mapSettings: getMapSettings(state),
});

const mapDispatchToProps = {
  onFetchData: fetchUpcomingActivitiesCardData,
  onHideDrillDown: hideUpcomingActivitiesDrillDown,
  onShowDrillDown: showUpcomingActivitiesDrillDown.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardContentComponent);
